import React from 'react';
import PropTypes from 'prop-types';

import backend from 'utils/backend';
import {dispatch, connect} from 'utils/state_manager';

import moment from 'moment';

import {Box, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import TaskItem from './components/TaskItem';
import TaskListSkeleton from './components/TaskListSkeleton';
import {TaskModal} from 'views/module';

const AllTasks = (props) => {
  const {
    user,
    taskRefresh,
    taskRefreshBump,
    statusProgressWrap: progressWrap,
  } = props;

  const [tasks, setTasks] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [model, setModel] = React.useState({});
  const [clientNameByRn, setClientNameByRn] = React.useState({});

  const todayTasks = React.useMemo(
      () => {
        const startOfTodayUtc = moment().startOf('day').utc();
        const endOfTodayUtc = moment().endOf('day').utc();

        return tasks.filter((task) => {
          const dueDate = moment(task.dueDate, moment.ISO_8601, true);
          if (!dueDate.isValid()) {
            return false;
          }

          return dueDate.isBefore(startOfTodayUtc) ||
            dueDate.isBetween(startOfTodayUtc, endOfTodayUtc, null, '[]');
        });
      },
      [tasks],
  );

  const thisWeekTasks = React.useMemo(
      () => {
        const startOfTomorrowUtc = moment().add(1, 'day').startOf('day').utc();
        const endOfWeekUtc = moment().endOf('isoWeek').utc();

        return tasks.filter((task) => {
          const dueDate = moment(task.dueDate, moment.ISO_8601, true);
          return dueDate.isValid() && dueDate.isBetween(
              startOfTomorrowUtc,
              endOfWeekUtc,
              null,
              '[]',
          );
        });
      },
      [tasks],
  );

  const dueTodayCount = React.useMemo(() => todayTasks.length, [todayTasks]);

  const isTaskInTodaySection = (task) => {
    const dueDate = moment(task?.dueDate, moment.ISO_8601, true);
    if (!dueDate.isValid()) {
      return false;
    }

    const startOfTodayUtc = moment().startOf('day').utc();
    const endOfTodayUtc = moment().endOf('day').utc();
    return dueDate.isBefore(startOfTodayUtc) ||
      dueDate.isBetween(startOfTodayUtc, endOfTodayUtc, null, '[]');
  };

  const isTaskInThisWeekSection = (task) => {
    const dueDate = moment(task?.dueDate, moment.ISO_8601, true);
    if (!dueDate.isValid()) {
      return false;
    }

    const startOfTomorrowUtc = moment().add(1, 'day').startOf('day').utc();
    const endOfWeekUtc = moment().endOf('isoWeek').utc();
    return dueDate.isBetween(startOfTomorrowUtc, endOfWeekUtc, null, '[]');
  };

  const processTaskList = (taskList) => {
    const sortedTasksWithDueDate = taskList
        .filter((task) => moment(task.dueDate, moment.ISO_8601, true).isValid())
        .sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());

    return sortedTasksWithDueDate;
  };

  const loadTasks = React.useCallback(() => {
    if (!user?.userRn) {
      setTasks([]);
      setClientNameByRn({});
      return;
    }

    setIsLoading(true);

    progressWrap(async () => {
      try {
        const startOfRangeUtc = moment.utc(0).toISOString();
        const endOfWeekUtc = moment().endOf('isoWeek').utc().toISOString();
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const [allTaskResponse, clientResponse] = await Promise.all([
          backend.workflowEngine.getAllTasks(startOfRangeUtc, endOfWeekUtc),
          backend.client.queryClient([], sortBy),
        ]);
        const allTasks = allTaskResponse?.list || [];
        const clientMap = (clientResponse?.list ?? []).reduce((acc, client) => {
          if (client?.clientRn) {
            acc[client.clientRn] = client.clientName || client.clientRn;
          }
          return acc;
        }, {});
        const processedTaskList = processTaskList(allTasks);
        setClientNameByRn(clientMap);
        setTasks(processedTaskList);
      } finally {
        setIsLoading(false);
      }
    });
  }, [user?.userRn, progressWrap, taskRefresh]);

  React.useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleAddTask = (taskData) => {
    progressWrap(async () => {
      try {
        const createdTask = await backend.workflowEngine.addTask(taskData);
        const shouldAppend = isTaskInTodaySection(createdTask) || isTaskInThisWeekSection(createdTask);

        if (shouldAppend) {
          setTasks((previousTasks) =>
            processTaskList([
              ...previousTasks.filter((task) => task.taskRn !== createdTask.taskRn),
              createdTask,
            ]),
          );
        }
      } finally {
        setModel({});
      }
    });
  };

  const openDialogs = {
    close: () => {
      setModel({});
    },
    addTask: () => {
      setModel({action: 'ADD_TASK'});
    },
    editTask: (task) => {
      setModel({action: 'EDIT_TASK', task});
    },
  };

  const edits = {
    editTask: (taskRn, taskData) => {
      progressWrap(async () => {
        try {
          await backend.workflowEngine.updateTask(taskRn, taskData);
          taskRefreshBump();
        } finally {
          setModel({});
          loadTasks();
        }
      });
    },
    removeTask: (taskData) => {
      progressWrap(async () => {
        try {
          await backend.workflowEngine.removeTask(taskData.taskRn);
          setTasks((previousTasks) =>
            previousTasks.filter((task) => task.taskRn !== taskData.taskRn),
          );
        } finally {
          setModel({});
        }
      });
    },
  };

  const popModelGenerator = () => {
    let popModel = null;

    switch (model.action) {
      case 'ADD_TASK':
        popModel = (
          <TaskModal
            mode='add'
            onClose={openDialogs.close}
            addTask={handleAddTask}
            askConnections
          />
        );
        break;
      case 'EDIT_TASK':
        popModel = (
          <TaskModal
            mode='edit'
            onClose={openDialogs.close}
            editTask={edits.editTask}
            task={model.task}
            askStatus
            askConnections
          />
        );
        break;
    }

    return popModel;
  };

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 4,
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: '100%',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
            }}
          >
            {translate('tasks.AllTasks.Header.Title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {translate('tasks.AllTasks.Header.Summary', {totalTasks: tasks.length, dueToday: dueTodayCount})}
          </Typography>
        </Stack>
      </Stack>
      <Stack>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontWeight: 700,
            fontSize: '16px',
            color: 'grey.500',
          }}
        >
          {translate('tasks.AllTasks.Sections.Today')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) auto',
            columnGap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          {
            isLoading && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <TaskListSkeleton />
              </Box>
            )
          }
          {
            !isLoading && todayTasks.length === 0 && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {translate('tasks.AllTasks.Empty')}
                </Typography>
              </Box>
            )
          }
          {
            !isLoading && todayTasks.map((task) => (
              <TaskItem
                key={task.taskRn}
                task={task}
                clientNameByRn={clientNameByRn}
                removeTask={edits.removeTask}
              />
            ))
          }
          <Box sx={{gridColumn: '1 / -1'}}>
            <Box
              component='button'
              onClick={openDialogs.addTask}
              sx={{
                background: 'none',
                border: 'none',
                color: '#A8A49E',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '14px',
                p: 0,
                mt: 1,
              }}
            >
              <Box component='span'>+</Box>
              <Translate expression='tasks.Common.NewTask' />
            </Box>
          </Box>
        </Box>
      </Stack>
      <Stack>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontWeight: 700,
            fontSize: '16px',
            color: 'grey.500',
          }}
        >
          {translate('tasks.AllTasks.Sections.ThisWeek')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) auto',
            columnGap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          {
            isLoading && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <TaskListSkeleton />
              </Box>
            )
          }
          {
            !isLoading && thisWeekTasks.length === 0 && (
              <Box sx={{gridColumn: '1 / -1'}}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {translate('tasks.AllTasks.Empty')}
                </Typography>
              </Box>
            )
          }
          {
            !isLoading && thisWeekTasks.map((task) => (
              <TaskItem
                key={task.taskRn}
                task={task}
                clientNameByRn={clientNameByRn}
                removeTask={edits.removeTask}
              />
            ))
          }
          <Box sx={{gridColumn: '1 / -1'}}>
            <Box
              component='button'
              onClick={openDialogs.addTask}
              sx={{
                background: 'none',
                border: 'none',
                color: '#A8A49E',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '14px',
                p: 0,
                mt: 1,
              }}
            >
              <Box component='span'>+</Box>
              <Translate expression='tasks.Common.NewTask' />
            </Box>
          </Box>
        </Box>
      </Stack>
      {popModelGenerator()}
    </Stack>
  );
};

AllTasks.propTypes = {
  user: PropTypes.object,
  taskRefresh: PropTypes.number,
  taskRefreshBump: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  taskRefresh: state.taskRefresh?.value,
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(AllTasks));
